/* import __COLOCATED_TEMPLATE__ from './graph.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { GraphOptions } from '@ebiz/ember-aditools-graph';
import type IntlService from 'ember-intl/services/intl';

interface GraphFigureArgs {
  model: GraphModel;
  choice: ChoiceDetails;
  options: GraphOptions;
  displayWidth: number;
  displayHeight: number;
  updateInput?: (inputType: string, modeldata: string) => void;
  showHelp?: (title: string, message: string, msgparams?: Record<string, unknown>) => void;
}

export type GraphModel = {
  data: Array<number>[];
  limits: { [key in 'xmin' | 'xmax' | 'ymin' | 'ymax']: number };
};

export type ChoiceDetails = {
  name: string;
  options: GraphOptions;
};

// Changes to the options getter should also be applied to the adjustedOptions getter on the noise figure
export default class GraphFigure extends Component<GraphFigureArgs> {
  @service declare intl: IntlService;

  // Tweak options so that a -1 on yticks becomes 9 for graphs with narrow ranges and 1 for broad ranges.
  get options(): GraphOptions {
    let yticks = this.args.options.yticks;
    let xticks = this.args.options.xticks;
    if (this.args.options.yscale === 'log' && this.args.options.yticks === -1) {
      const breadth = Math.log10(this.args.model.limits.ymax) - Math.log10(this.args.model.limits.ymin);
      //console.log(`Decades of y for ${this.args.choice.name}: `, breadth);
      yticks = breadth > 3.5 ? 1 : breadth > 1.5 ? 3 : 9;
    }
    if (this.args.options.xscale === 'log' && this.args.options.xticks === -1) {
      const breadth = Math.log10(this.args.model.limits.xmax) - Math.log10(this.args.model.limits.xmin);
      //console.log(`Decades of x for ${this.args.choice.name}: `, breadth);
      xticks = breadth > 3.5 ? 1 : breadth > 1.5 ? 3 : 9;
    }
    // We can't afford to overwrite anything in our original
    const result = Object.assign({}, this.args.options); //clone input
    result.yticks = yticks; //tweak clone
    result.xticks = xticks;

    // Add hover section if available
    result.hover = this.args.options.hover;
    if (result.hover && this.args.options) {
      result.xUnit = this.args.options.xUnit ? this.intl.t(this.args.options.xUnit) : undefined;
      result.yUnit = this.args.options.yUnit ? this.intl.t(this.args.options.yUnit) : undefined;
      if (this.args.options.hoverTitles) {
        const headerLabel = this.args.options.hoverTitles.headerLabel
          ? this.intl.t(this.args.options.hoverTitles.headerLabel)
          : undefined;
        const lineLabels = [];
        for (const lineLabel of this.args.options.hoverTitles.lineLabels) {
          lineLabels.push(this.intl.t(lineLabel));
        }
        if (headerLabel && lineLabels.length > 0) {
          result.hoverTitles = {
            headerLabel: headerLabel,
            lineLabels: lineLabels,
          };
        }
      }
      result.hoverWidthAdjust = this.hoverWidth;
    }

    return result; //ship it
  }

  get hoverWidth(): number | undefined {
    switch (this.intl.primaryLocale) {
      case 'en-us':
      case 'ru-ru':
        return 1.05;
      case 'zh-cn':
        return 1.3;
      case 'ja-jp':
        return 1.72;
      default:
        return;
    }
  }
}
